import React from 'react';
import { FormattedMessage } from 'react-intl';
import { AiFillRuntimeInput } from 'api/ai-fill/types';
import { TimesheetCalendarSourceType } from 'api/timesheets';
import { CustomLayoutDrawerContent } from 'components/atoms/drawer';
import { useGetMeetingSettings } from 'queries/meetings';
import { useTimesheetIntegrationsQuery } from 'queries/timesheets/calendar-integration-provider';
import { getCalendarSourceTypeFromAiFillSettings } from 'vendor/ckeditor/react-components/ai-fill/utils';
import { AIDrawerContentLoading } from './ai-drawer-loading';
import { ReAuthCalendar } from './calendar/re-auth';
import { SetupCalendar } from './calendar/setup';
import { SelectMeetings } from './select-meetings';

interface Props {
  onBack: () => void;
  onClose: () => void;
  onChooseTemplate: (aiFillConfig?: AiFillRuntimeInput) => void;
}

export const AIDrawerContent = ({
  onBack,
  onClose,
  onChooseTemplate,
}: Props) => {
  const { data: aiFillSettings, isLoading: isAiSettingsLoading } =
    useGetMeetingSettings();

  const { data: integrations, isLoading } = useTimesheetIntegrationsQuery(
    aiFillSettings?.calendar ?? TimesheetCalendarSourceType.GoogleCalendar,
    {
      enabled:
        !!aiFillSettings &&
        aiFillSettings?.aiFillsEnabled &&
        !isAiSettingsLoading,
    },
  );

  const integration = integrations?.find(
    (meta) => meta.integrationStatus === 'ACTIVE',
  );
  const expiredIntegration = integrations?.find(
    (meta) => meta.integrationStatus === 'AUTHENTICATION_EXPIRED',
  );

  if (isLoading || isAiSettingsLoading || !aiFillSettings) {
    return <AIDrawerContentLoading />;
  }

  const check = aiFillSettings?.gongEnabled ? false : !integration;

  return (
    <>
      {check ? (
        <>
          <CustomLayoutDrawerContent.Title>
            <CustomLayoutDrawerContent.Title.Description>
              <FormattedMessage id="ai_fill.meetings.select.drawer.description" />
            </CustomLayoutDrawerContent.Title.Description>
            <FormattedMessage id="ai_fill.meetings.select.drawer.not_connected.title" />
          </CustomLayoutDrawerContent.Title>
          <CustomLayoutDrawerContent.Content>
            {expiredIntegration ? (
              <ReAuthCalendar
                sourceType={getCalendarSourceTypeFromAiFillSettings(
                  aiFillSettings,
                )}
                expiredIntegration={expiredIntegration}
              />
            ) : (
              <SetupCalendar
                sourceType={getCalendarSourceTypeFromAiFillSettings(
                  aiFillSettings,
                )}
              />
            )}
          </CustomLayoutDrawerContent.Content>
        </>
      ) : (
        <SelectMeetings
          calendarIntegration={integration}
          onBack={onBack}
          onClose={onClose}
          onFinish={onChooseTemplate}
        />
      )}
    </>
  );
};
